import { useState } from "react";
import SplitSuggestion from "./SplitSuggestion";

const CURRENCY_SYMBOL = { EUR: "€", USD: "US$", GBP: "£" };
const symbolFor = (c) => CURRENCY_SYMBOL[c] || c;

// Los 4 tipos de coste (solo textos de ayuda; la mecánica de cálculo no cambia).
export const COST_TYPE_INFO = {
  service_base: {
    label: "Base (todos los clientes)",
    description: "Coste para servir a todos tus clientes (se reparte entre los clientes facturados ese mes)",
  },
  client_direct: {
    label: "Cliente directo",
    description: "Coste directo de un cliente concreto (baja su margen)",
  },
  own_investment: {
    label: "Inversión propia",
    description: "Inversión propia del negocio (no se imputa a clientes)",
  },
  operational: {
    label: "Operativo",
    description: "Gasto operativo general (estructura)",
  },
};

export default function SplitPopover({ totalEur, currency = "EUR", initialLines, clients, services, onSave, onDelete, onCancel, suggestion = null }) {
  const isNative = currency !== "EUR";
  const sym = symbolFor(currency);
  const [lines, setLines] = useState(
    initialLines.length > 0
      ? initialLines.map(l => ({
          cost_type: l.cost_type,
          client_id: l.client_id || "",
          service_id: l.service_id || "",
          amount_eur: l.amount_eur,
          notes: l.notes || "",
        }))
      : [{ cost_type: "service_base", client_id: "", service_id: "", amount_eur: totalEur, notes: "" }]
  );

  const assigned = lines.reduce((acc, l) => acc + (parseFloat(l.amount_eur) || 0), 0);
  const balanced = Math.abs(assigned - totalEur) <= 0.01;

  // 3.3 — la sugerencia se ofrece mientras NO haya split guardado
  // (initialLines vacías), aunque el usuario ya haya tocado las líneas:
  // «Aplicar» REEMPLAZA las líneas por la sugerencia; guardar sigue siendo
  // decisión humana (el botón Guardar, deshabilitado si no cuadra).
  // Si la sugerencia coincide con lo que ya hay (la línea por defecto es Base
  // 100 %, igual que la sugerencia más habitual) se muestra «Coincide» sin
  // botón, en vez de un Aplicar que no hace nada (smoke 30-08-2026, D2).
  const sameLine = (a, b) =>
    a.cost_type === b.cost_type &&
    (a.client_id || "") === (b.client_id || "") &&
    (a.service_id || "") === (b.service_id || "") &&
    Math.abs((parseFloat(a.amount_eur) || 0) - (b.amount_eur ?? 0)) <= 0.01;
  const matchesSuggestion =
    Boolean(suggestion?.lines?.length) &&
    lines.length === suggestion.lines.length &&
    lines.every((l, i) => sameLine(l, suggestion.lines[i]));

  const applySuggestion = () => {
    setLines(suggestion.lines.map(l => ({
      cost_type: l.cost_type,
      client_id: l.client_id || "",
      service_id: l.service_id || "",
      amount_eur: l.amount_eur ?? 0,
      notes: "",
    })));
  };

  const updateLine = (i, patch) => {
    setLines(ls => ls.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  };
  const addLine = () => {
    // No clampear a 0: si la factura es negativa (nota de crédito), el remaining también lo es.
    const remaining = totalEur - assigned;
    setLines(ls => [...ls, { cost_type: "service_base", client_id: "", service_id: "", amount_eur: remaining, notes: "" }]);
  };
  const removeLine = (i) => {
    if (lines.length <= 1) return;
    setLines(ls => ls.filter((_, idx) => idx !== i));
  };

  const handleSave = async () => {
    if (!balanced) return;
    const payload = {
      lines: lines.map(l => ({
        cost_type: l.cost_type,
        client_id: l.cost_type === "client_direct" ? (l.client_id || null) : null,
        service_id: l.cost_type === "client_direct" ? (l.service_id || null) : null,
        amount_eur: parseFloat(l.amount_eur),
        notes: l.notes || null,
      }))
    };
    await onSave(payload);
  };

  return (
    <div className="p-3 space-y-2 w-[560px] max-w-[90vw]">
      {suggestion && initialLines.length === 0 && (
        <SplitSuggestion
          suggestion={suggestion}
          clients={clients}
          services={services}
          matches={matchesSuggestion}
          onApply={applySuggestion}
        />
      )}
      {lines.map((l, i) => (
        <div key={i} className="space-y-0.5">
          <div className="grid grid-cols-[140px_1fr_1fr_90px_24px] gap-1 items-center">
            <select value={l.cost_type} onChange={e => updateLine(i, { cost_type: e.target.value, client_id: "", service_id: "" })}
                    className="border rounded px-1 py-0.5 text-sm"
                    data-testid={`split-cost-type-${i}`}
                    title={COST_TYPE_INFO[l.cost_type]?.description}>
              {Object.entries(COST_TYPE_INFO).map(([value, info]) => (
                <option key={value} value={value} title={info.description}>{info.label}</option>
              ))}
            </select>
            {l.cost_type === "client_direct" ? (
              <>
                <select value={l.client_id} onChange={e => updateLine(i, { client_id: e.target.value, service_id: "" })}
                        className="border rounded px-1 py-0.5 text-sm">
                  <option value="">— cliente —</option>
                  {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
                </select>
                <select value={l.service_id} onChange={e => updateLine(i, { service_id: e.target.value })}
                        className="border rounded px-1 py-0.5 text-sm" disabled={!l.client_id}>
                  <option value="">— servicio —</option>
                  {services.filter(s => s.client_id === l.client_id).map(s =>
                    <option key={s.service_id} value={s.service_id}>{s.service_name}</option>)}
                </select>
              </>
            ) : (
              <><span /><span /></>
            )}
            <input type="number" step="0.01" value={l.amount_eur}
                   onChange={e => updateLine(i, { amount_eur: e.target.value })}
                   className="border rounded px-1 py-0.5 text-sm text-right" />
            <button type="button" onClick={() => removeLine(i)} disabled={lines.length <= 1}
                    className="text-red-500 disabled:text-gray-300">✕</button>
          </div>
          {COST_TYPE_INFO[l.cost_type]?.description && (
            <p className="text-[11px] text-slate-500 leading-snug">{COST_TYPE_INFO[l.cost_type].description}</p>
          )}
        </div>
      ))}
      <button type="button" onClick={addLine}
              disabled={balanced}
              className="text-blue-600 text-sm disabled:text-gray-400">+ Añadir línea</button>
      {isNative && (
        <div className="text-xs text-amber-600">
          Importes en {sym} — el EUR real se ajustará al conciliar con el banco.
        </div>
      )}
      <div className={`text-sm ${balanced ? "text-green-600" : "text-red-600"}`}>
        Asignado: {assigned.toFixed(2)} {sym} / {totalEur.toFixed(2)} {sym} {balanced ? "✓" : `— faltan ${(totalEur - assigned).toFixed(2)} ${sym}`}
      </div>
      <div className="flex justify-between gap-2">
        {initialLines.length > 0 && (
          <button type="button" onClick={onDelete} className="text-red-600 text-sm">Quitar asignación</button>
        )}
        <div className="flex gap-2 ml-auto">
          <button type="button" onClick={onCancel} className="px-2 py-1 text-sm">Cancelar</button>
          <button type="button" onClick={handleSave} disabled={!balanced}
                  className="px-2 py-1 text-sm bg-blue-600 text-white rounded disabled:bg-gray-300">Guardar</button>
        </div>
      </div>
    </div>
  );
}
